import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DetailSection } from '../ui/DetailSection.js';
import { SectionHeaderAction } from '../ui/SectionHeaderAction.js';
import { DataTable, type ColumnDef } from '../ui/DataTable.js';
import { EmptyState } from '../ui/EmptyState.js';
import { LoadingState } from '../ui/LoadingState.js';
import { Pressable } from '../ui/Pressable.js';
import { Pill } from '../PillGroup.js';
import { formatMoney } from '../../lib/money.js';
import { EstimateDetail, ESTIMATE_STATUS_TONES } from '../billing/EstimateDetail.js';
import { useEstimates, useInvoices, useAccounts, type Estimate, type Invoice } from '../../hooks/useBilling.js';

interface EstimatesSectionProps {
  projectId: number;
  contactId: number;
}

export function EstimatesSection({ projectId, contactId }: EstimatesSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Only 'new' shows an inline form here — viewing/editing an EXISTING
  // estimate or invoice navigates to the standalone billing module instead,
  // same as how the project's Reports tab hands off to /reports/:id
  // (ProjectDetailPage.tsx).
  const [creating, setCreating] = useState(false);
  const { data: estimatesData, isLoading } = useEstimates({ projectId });
  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices({ projectId });
  const { data: accountsData } = useAccounts();
  const estimates = estimatesData?.estimates ?? [];
  const invoices = invoicesData?.invoices ?? [];
  const accounts = accountsData?.accounts ?? [];

  function currencyFor(accId: number) {
    return accounts.find(a => a.id === accId)?.currency ?? 'usd';
  }

  if (creating) {
    return (
      <DetailSection
        title={t('billing.estimates.create')}
        divider={false}
        titleRight={
          <SectionHeaderAction tone="muted" onClick={() => setCreating(false)}>
            {t('billing.estimates.backToList')}
          </SectionHeaderAction>
        }
      >
        <EstimateDetail id={null} projectId={projectId} contactId={contactId} accounts={accounts} />
      </DetailSection>
    );
  }

  const columns: ColumnDef<Estimate>[] = [
    {
      accessorKey: 'number',
      header: t('billing.estimates.columns.number'),
      cell: ({ row }) => (
        <Pressable onPress={() => navigate(`/facturacion/estimate/${row.original.id}`)}>
          <span style={{ color: 'var(--evo-cyan)' }}>{row.original.number ?? `#${row.original.id}`}</span>
        </Pressable>
      ),
    },
    {
      accessorKey: 'status',
      header: t('billing.estimates.columns.status'),
      cell: ({ row }) => (
        <Pill tone={ESTIMATE_STATUS_TONES[row.original.status] ?? 'neutral'} variant="outline" size="sm">
          {t(`billing.estimateStatus.${row.original.status}`, { defaultValue: row.original.status })}
        </Pill>
      ),
    },
    {
      accessorKey: 'total',
      header: t('billing.estimates.columns.total'),
      cell: ({ row }) => formatMoney(row.original.total, currencyFor(row.original.accountId)),
    },
    {
      accessorKey: 'validUntil',
      header: t('billing.estimates.columns.validUntil'),
      cell: ({ row }) => row.original.validUntil ?? '—',
    },
    {
      accessorKey: 'createdAt',
      header: t('billing.estimates.columns.createdAt'),
      cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
    },
  ];

  const invoiceColumns: ColumnDef<Invoice>[] = [
    {
      accessorKey: 'number',
      header: t('billing.estimates.columns.number'),
      cell: ({ row }) => (
        <Pressable onPress={() => navigate(`/facturacion/invoice/${row.original.id}`)}>
          <span style={{ color: 'var(--evo-cyan)' }}>{row.original.number ?? `#${row.original.id}`}</span>
        </Pressable>
      ),
    },
    {
      accessorKey: 'status',
      header: t('billing.estimates.columns.status'),
      cell: ({ row }) => (
        <Pill tone={ESTIMATE_STATUS_TONES[row.original.status] ?? 'neutral'} variant="outline" size="sm">
          {t(`billing.invoiceStatus.${row.original.status}`, { defaultValue: row.original.status })}
        </Pill>
      ),
    },
    {
      accessorKey: 'total',
      header: t('billing.estimates.columns.total'),
      cell: ({ row }) => formatMoney(row.original.total, row.original.currency),
    },
    {
      accessorKey: 'dueDate',
      header: t('billing.facturacion.dueDate'),
      cell: ({ row }) => row.original.dueDate ?? '—',
    },
    {
      accessorKey: 'createdAt',
      header: t('billing.estimates.columns.createdAt'),
      cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <>
      <DetailSection
        title={t('billing.estimates.title')}
        divider={false}
        titleRight={
          <SectionHeaderAction onClick={() => setCreating(true)}>
            {t('billing.estimates.create')}
          </SectionHeaderAction>
        }
      >
        {isLoading ? (
          <div style={{ padding: 'var(--evo-space-4) 0' }}><LoadingState size="sm" /></div>
        ) : estimates.length === 0 ? (
          <EmptyState title={t('billing.estimates.empty')} />
        ) : (
          <DataTable columns={columns} data={estimates} size="sm" pagination={false} getRowId={e => String(e.id)} />
        )}
      </DetailSection>

      <DetailSection title={t('billing.facturacion.viewInvoices')}>
        {invoicesLoading ? (
          <div style={{ padding: 'var(--evo-space-4) 0' }}><LoadingState size="sm" /></div>
        ) : invoices.length === 0 ? (
          <EmptyState title={t('billing.facturacion.noInvoices')} />
        ) : (
          <DataTable columns={invoiceColumns} data={invoices} size="sm" pagination={false} getRowId={i => String(i.id)} />
        )}
      </DetailSection>
    </>
  );
}
